/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { UserFinderListItem } from '@gh/builder-shared';
import {
  SalespersonBudgetListItemDto,
  SalespersonBudgetType,
  UserStatus,
} from '@gh/builder-shared-data';
import {
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  getInvalidFields,
  NumberTypes,
  setEntityField,
} from '@gh/common-shared';
import { EntityVersion, ID, WithDirtyType, WithEntityState } from '@gh/common-shared-data';
import { currentYear, MONTH_LOGICAL_NAMES } from '@gh/core-util';
import { first, fromPairs, isNil, omit, range, sortBy } from 'lodash';

export enum SbListItemType { Item, FooterSummary, FooterTitle }

export type SbListItem = WithEntityState<WithDirtyType<{
  type: SbListItemType;
  rowId: ID;
  salespersonBudgetId?: ID;
  salespersonName: string;
  salespersonId: ID;
  status: UserStatus;
  budgetYear: number;
  budgetType: SalespersonBudgetType;
  version?: EntityVersion;
  group: string;
  january?: number;
  february?: number;
  march?: number;
  april?: number;
  may?: number;
  june?: number;
  july?: number;
  august?: number;
  september?: number;
  october?: number;
  november?: number;
  december?: number;
  total?: number;
}>>;

export const SB_YEARS = range(-1, 2, 1).map((shift) => currentYear() + shift);
export const MONTH_LOGICAL_NAMES_WITH_TOTAL = MONTH_LOGICAL_NAMES.concat('total');

const SB_LIST_ITEM_ENTITY_CONFIG: EntityConfig<SbListItem> = fromPairs(MONTH_LOGICAL_NAMES.map((month) => [month, {
  required: true,
  editable: true,
  type: NumberTypes.Count,
}]));

export const sbListItemFieldState = fieldStateByEntityConfig(SB_LIST_ITEM_ENTITY_CONFIG);
export const validateSbListItem = entityValidatorByEntityConfig(SB_LIST_ITEM_ENTITY_CONFIG);
export const validateSbListItemField = fieldValidatorByEntityConfig(SB_LIST_ITEM_ENTITY_CONFIG);
export const calculateSbListItemId = (type: SalespersonBudgetType, year: number, id: ID) => `${type}-${year}-${id}`;
export const calculateSbListItemGroupKey = (type: SalespersonBudgetType, year: number) => `${type}-${year}`;

export const getSbListItemId = (item: SbListItem) => item.rowId;

export const sbListItemFromDto = (dto: SalespersonBudgetListItemDto) =>
  mergeSbItemTotal(<SbListItem>({
    ...omit(dto, 'budgetByMonth'),
    type: SbListItemType.Item,
    rowId: calculateSbListItemId(dto.budgetType, dto.budgetYear, dto.salespersonId),
    group: calculateSbListItemGroupKey(dto.budgetType, dto.budgetYear),
    ...fromPairs(MONTH_LOGICAL_NAMES.map((month, i) => [month, dto.budgetByMonth[i]])),
  }));
export const sbListItemToSaveDto = (item: SbListItem) => <SalespersonBudgetListItemDto>({
  ...omit(
    item,
    ['group', 'rowId', 'total', 'type', 'entityState'],
    MONTH_LOGICAL_NAMES,
    ['salespersonName', 'status']),
  budgetByMonth: MONTH_LOGICAL_NAMES.map((month) => item[month]),
});
export const sbBudgetFromDto = (dtos: SalespersonBudgetListItemDto[]) => dtos.map(sbListItemFromDto);
export const sbBudgetToSaveDto = (items: SbListItem[]) => items.map(sbListItemToSaveDto);
export const sbItemFromUser = (user: UserFinderListItem, budgetType: SalespersonBudgetType, budgetYear: number) =>
  <SbListItem>({
    type: SbListItemType.Item,
    rowId: calculateSbListItemId(budgetType, budgetYear, user.userId),
    salespersonId: user.userId,
    salespersonName: user.userName,
    status: user.statusValue,
    budgetYear,
    budgetType,
    group: calculateSbListItemGroupKey(budgetType, budgetYear),
    ...fromPairs(MONTH_LOGICAL_NAMES.map((month) => [month, void 0])),
  });

export const getSbItemFirstInvalidField = (item: SbListItem) => {
  const indexOfFirstInvalidField = first(sortBy(getInvalidFields(item)
    .map((field) => MONTH_LOGICAL_NAMES.indexOf(field))
    .filter((index) => index >= 0)));

  return isNil(indexOfFirstInvalidField) ? void 0 : MONTH_LOGICAL_NAMES[indexOfFirstInvalidField];
};

export const mergeSbItemTotal = (item: SbListItem) =>
  setEntityField(item, 'total', MONTH_LOGICAL_NAMES.reduce((sum, month) => sum + (item[month] || 0), 0));
export const calculateSbFooterItem = (items: SbListItem[]) => items.reduce(
  (footer, item) => {
    MONTH_LOGICAL_NAMES_WITH_TOTAL.forEach((month) => footer[month] += item[month] || 0);
    return footer;
  },
  <SbListItem>{
    type: SbListItemType.FooterSummary,
    rowId: 'footer-total',
    salespersonName: '',
    total: 0, ...fromPairs(MONTH_LOGICAL_NAMES.map((month) => [month, 0])),
  });
